import { useState } from "react";
import { Link } from "react-router-dom";
import LayoutWide from "../layouts/LayoutWide.jsx";
import Spinner from "../components/Spinner.jsx";
import { useRecipesQuery, useDeleteRecipe } from "../hooks/useRecipesQuery.js";

function ingredientSummary(raw) {
  if (!raw) return null;
  try {
    const rows = JSON.parse(raw);
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const names = rows.filter(r => r.name?.trim()).map(r => r.name.trim());
    if (names.length === 0) return null;
    const preview = names.slice(0, 3).join(", ");
    return names.length > 3 ? `${preview} +${names.length - 3} more` : preview;
  } catch {
    return null;
  }
}

const TYPE_LABELS = {
  liquid_culture: "Liquid Culture",
  bulk_spawn:     "Bulk Spawn",
  substrate:      "Substrate",
  other:          "Other",
};

const TYPE_COLORS = {
  liquid_culture: "bg-blue-100 text-blue-700",
  bulk_spawn:     "bg-amber-100 text-amber-700",
  substrate:      "bg-emerald-100 text-emerald-700",
  other:          "bg-gray-100 text-gray-600",
};

export default function Recipes() {
  const { data: recipes, isLoading, isError } = useRecipesQuery();
  const deleteRecipe = useDeleteRecipe();
  const [confirmId, setConfirmId] = useState(null);

  if (isLoading) return <LayoutWide title="Recipes"><Spinner /></LayoutWide>;
  if (isError) return <LayoutWide title="Recipes"><div className="p-4 text-red-600">Failed to load recipes.</div></LayoutWide>;

  const grouped = {};
  for (const r of (recipes ?? [])) {
    const t = r.recipe_type;
    if (!grouped[t]) grouped[t] = [];
    grouped[t].push(r);
  }
  const typeOrder = ["liquid_culture", "bulk_spawn", "substrate", "other"];

  async function handleDelete(id) {
    await deleteRecipe.mutateAsync(id);
    setConfirmId(null);
  }

  return (
    <LayoutWide title="Recipes">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-cyan-800">Recipes</h1>
        <Link
          to="/recipes/new"
          className="text-sm bg-cyan-600 text-white rounded px-3 py-1.5 hover:bg-cyan-700 transition-colors font-medium"
        >
          + New Recipe
        </Link>
      </div>

      {(recipes ?? []).length === 0 && (
        <div className="text-center py-20 text-gray-400">
          <div className="text-5xl mb-4">🧪</div>
          <div className="text-lg font-medium text-gray-500 mb-2">No recipes yet</div>
          <div className="text-sm mb-6">Build up your recipe library — liquid cultures, substrates, spawn formulas.</div>
          <Link
            to="/recipes/new"
            className="text-sm bg-cyan-600 text-white rounded px-4 py-2 hover:bg-cyan-700 transition-colors font-medium"
          >
            Add your first recipe
          </Link>
        </div>
      )}

      {typeOrder.filter(t => grouped[t]?.length).map(type => (
        <div key={type} className="mb-8">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
            {TYPE_LABELS[type]}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {grouped[type].map(recipe => (
              <div key={recipe.id} className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-2 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/recipes/${recipe.id}`} className="font-medium text-gray-800 hover:text-cyan-700 leading-tight">
                    {recipe.name}
                  </Link>
                  <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLORS[recipe.recipe_type]}`}>
                    {TYPE_LABELS[recipe.recipe_type]}
                  </span>
                </div>

                {ingredientSummary(recipe.ingredients) && (
                  <p className="text-sm text-gray-500 line-clamp-2">{ingredientSummary(recipe.ingredients)}</p>
                )}

                <div className="flex items-center gap-3 mt-auto pt-1">
                  <Link to={`/recipes/${recipe.id}`} className="text-xs text-cyan-600 hover:underline">View</Link>
                  <Link to={`/recipes/${recipe.id}/edit`} className="text-xs text-gray-500 hover:underline">Edit</Link>
                  {confirmId === recipe.id ? (
                    <span className="text-xs text-red-600 flex items-center gap-1">
                      Delete?
                      <button onClick={() => handleDelete(recipe.id)} className="font-semibold hover:underline">Yes</button>
                      <span>/</span>
                      <button onClick={() => setConfirmId(null)} className="hover:underline">No</button>
                    </span>
                  ) : (
                    <button onClick={() => setConfirmId(recipe.id)} className="text-xs text-gray-400 hover:text-red-500 ml-auto">Delete</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </LayoutWide>
  );
}
